import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { API, errMsg, formatDate } from "@/lib/api";

export default function AdminAdmins() {
  const [users, setUsers] = useState(null);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [adding, setAdding] = useState(false);
  const [manualPw, setManualPw] = useState(null);

  const load = () => {
    API.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
    API.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const r = await API.post("/admin/users", form);
      if (r.data.temp_password) {
        setManualPw({ email: form.email, temp_password: r.data.temp_password });
      } else {
        toast.success(`Admin added — a temporary password has been emailed to ${form.email}`);
      }
      setForm({ email: "", name: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (email) => {
    try {
      await API.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const transfer = async (email) => {
    try {
      await API.post("/admin/users/transfer-ownership", { email });
      toast.success(`Ownership transferred to ${email}. You are now a standard admin.`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const isOwner = me?.role === "owner";

  return (
    <div data-testid="admin-users-page">
      <h1 className="font-display text-3xl tracking-tight mb-2">Admins</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Everyone listed here can manage the website. New admins receive a temporary password by email and set their own password on first login.
        The login verification code is emailed to all admins.
      </p>

      <Card className="mb-6" data-testid="add-admin-card">
        <CardContent className="p-6">
          <h2 className="font-display text-xl mb-4">Add an admin</h2>
          <form onSubmit={add} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-admin-email" className="sr-only">Email</Label>
              <Input id="new-admin-email" type="email" required placeholder="their@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email-input" />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-admin-name" className="sr-only">Name</Label>
              <Input id="new-admin-name" placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name-input" />
            </div>
            <Button type="submit" className="rounded-full" disabled={adding} data-testid="add-admin-btn">
              <Plus className="mr-2 h-4 w-4" /> {adding ? "Adding…" : "Add admin"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {manualPw && (
        <Card className="mb-6 border-amber-400 bg-amber-50 dark:bg-amber-950/40" data-testid="manual-temp-password-card">
          <CardContent className="p-6">
            <h2 className="font-display text-xl mb-2">Email couldn't be sent — share this manually</h2>
            <p className="text-sm text-muted-foreground mb-4">
              The account for <strong className="text-foreground">{manualPw.email}</strong> was created, but the temporary password email could not be delivered.
              Give them this temporary password securely (it is shown only once):
            </p>
            <p className="font-mono text-lg font-bold tracking-wider bg-background border rounded-lg px-4 py-3 inline-block" data-testid="manual-temp-password">{manualPw.temp_password}</p>
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={() => setManualPw(null)} data-testid="dismiss-manual-pw-btn">I've saved it</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!users ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : (
        <div className="space-y-3" data-testid="admins-list">
          {users.map((u) => (
            <Card key={u.email} data-testid={`admin-user-${u.email}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="p-2.5 rounded-full bg-primary/10 text-primary shrink-0"><ShieldCheck className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {u.name || u.email}
                    {u.role === "owner" && <Badge className="ml-2 rounded-full" data-testid={`owner-badge-${u.email}`}>Owner</Badge>}
                    {me?.email === u.email && <Badge variant="secondary" className="ml-2 rounded-full">You</Badge>}
                    {u.must_change_password && <Badge className="ml-2 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-0">Password not set yet</Badge>}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{u.email} · added {formatDate(u.created_at)}</p>
                </div>
                {isOwner && me?.email !== u.email && u.role !== "owner" && (
                  <div className="flex gap-1 shrink-0">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`transfer-owner-${u.email}`}>
                          Make owner
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Transfer ownership to {u.email}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            They become the owner with full control, including removing other admins. You will become a standard admin. This can only be reversed by the new owner.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => transfer(u.email)} data-testid={`confirm-transfer-${u.email}`}>Transfer ownership</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive" data-testid={`remove-admin-${u.email}`} aria-label="Remove admin">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
                          <AlertDialogDescription>They will immediately lose access to the admin panel.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(u.email)} data-testid={`confirm-remove-admin-${u.email}`}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
